"use client";

import { Accordion as BaseAccordion } from "@base-ui/react/accordion";
import { IconChevronDown } from "@tabler/icons-react";
import clsx from "clsx";
import type * as React from "react";
import "./Accordion.css";

export interface AccordionContentProps
  extends Omit<BaseAccordion.Panel.Props, "className" | "style"> {
  className?: string;
  style?: React.CSSProperties;
}

const AccordionContent = (props: AccordionContentProps) => {
  const { className, style, children, ...panelProps } = props;
  return (
    <BaseAccordion.Panel {...panelProps} className="calmlens-accordion-content">
      <div className={className} style={style}>
        {children}
      </div>
    </BaseAccordion.Panel>
  );
};

export interface AccordionTriggerProps extends BaseAccordion.Trigger.Props {
  ref?: React.Ref<HTMLButtonElement>;
}

const AccordionTrigger = (props: AccordionTriggerProps) => {
  const { className, children, ref, ...otherProps } = props;

  return (
    <BaseAccordion.Header className={"calmlens-accordion-header"}>
      <BaseAccordion.Trigger
        className={clsx("calmlens-accordion-trigger", className)}
        {...otherProps}
        ref={ref}
      >
        <span className="calmlens-accordion-heading">{children}</span>
      </BaseAccordion.Trigger>
    </BaseAccordion.Header>
  );
};

export interface AccordionItemProps extends Omit<BaseAccordion.Item.Props, "value"> {
  value: string | number;
}

const AccordionItem = (props: AccordionItemProps) => {
  const { className, value, ...otherProps } = props;
  return (
    <BaseAccordion.Item
      value={value as any}
      className={clsx("calmlens-accordion-item", className)}
      {...otherProps}
    />
  );
};

type AccordionValue = string | number | Array<string | number> | undefined;

export interface AccordionProps
  extends Omit<BaseAccordion.Root.Props, "defaultValue" | "multiple" | "onValueChange" | "value"> {
  type?: "single" | "multiple";
  /** `boxed` draws each item as a bordered card whose open panel joins its trigger. */
  variant?: "default" | "boxed";
  collapsible?: boolean;
  value?: AccordionValue;
  defaultValue?: AccordionValue;
  onValueChange?: (
    value: AccordionValue,
    eventDetails: BaseAccordion.Root.ChangeEventDetails,
  ) => void;
}

const Accordion = (props: AccordionProps) => {
  const {
    className,
    type,
    variant = "default",
    collapsible: _collapsible,
    value,
    defaultValue,
    onValueChange,
    ...otherProps
  } = props;
  const multiple = type === "multiple";
  const toBaseValue = (nextValue: AccordionValue) =>
    nextValue === undefined ? undefined : Array.isArray(nextValue) ? nextValue : [nextValue];
  return (
    <BaseAccordion.Root
      multiple={multiple}
      value={toBaseValue(value)}
      defaultValue={toBaseValue(defaultValue)}
      onValueChange={(nextValue, eventDetails) =>
        onValueChange?.(multiple ? nextValue : nextValue[0], eventDetails)
      }
      className={clsx("calmlens-accordion-root", className)}
      data-variant={variant}
      {...otherProps}
    />
  );
};

export interface AccordionIndicatorProps extends React.HTMLAttributes<HTMLSpanElement> {}

const AccordionIndicator = (props: AccordionIndicatorProps) => {
  const { className, children, ...otherProps } = props;
  return (
    <span
      aria-hidden={true}
      className={clsx("calmlens-accordion-indicator", className)}
      {...otherProps}
    >
      {children ?? <IconChevronDown />}
    </span>
  );
};

export default Object.assign(Accordion, {
  Item: AccordionItem,
  Trigger: AccordionTrigger,
  Content: AccordionContent,
  Indicator: AccordionIndicator,
});
